import React, { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  Typography,
  Box,
  Paper,
  makeStyles,
  CircularProgress,
  Button,
  Divider,
} from "@material-ui/core";
import ArrowBack from "@material-ui/icons/ArrowBack";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { appUrl } from "../../../../../../utils/config";

// Import step components individually
import ProductDetails from "../Stepper/steps/ProductDetails";
import StatusSelection from "../Stepper/steps/StatusSelection";
import AllocationConfigurations from "../Stepper/steps/AllocationConfigurations";
import DocumentDeliverables from "../Stepper/steps/DocumentDeliverables";
import OtherConfigurations from "../Stepper/steps/OtherConfigurations";
import ProcessFlowConfiguration from "../Stepper/steps/ProcessFlowConfiguration";
import ShipmentConfigurations from "../Stepper/steps/ShipmentConfigurations";
import ViewProductEdit from "./ViewProductEdit";
import CloseIcon from "@material-ui/icons/Close";
import { slocsFromMapping } from "../productSLocMapping";
import { getSponsorLinkedFlags } from "../materialSponsorLink";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0,
    width: "100%",
  },
  stepperHeader: {
    padding: "32px 0",
    textAlign: "center",
    backgroundColor: "#F5F9FF",
    position: "relative",
  },
  heading: {
    fontWeight: 500,
    fontSize: 30,
    lineHeight: "100%",
    marginBottom: 20,
  },
  subheading: {
    fontWeight: 400,
    fontSize: 16,
    lineHeight: "100%",
  },
  backButton: {
    display: "flex",
    alignItems: "center",
    color: "#ec6535",
    marginBottom: "1rem",
    marginTop: "1rem",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  title: {
    marginBottom: theme.spacing(3),
  },
  sectionPaper: {
    padding: 0,
    marginBottom: 0,
  },
  reviewSection: {
    marginBottom: 50,
    position: "relative",
  },
  actionButtons: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 0,
    marginBottom: 0,
  },
  editButton: {
    backgroundColor: "#ec6535",
    color: "white",
    "&:hover": {
      backgroundColor: "#d45328",
    },
  },
  saveButton: {
    backgroundColor: "#4caf50",
    color: "white",
    marginRight: theme.spacing(2),
    "&:hover": {
      backgroundColor: "#388e3c",
    },
  },
  discardButton: {
    backgroundColor: "#f44336",
    color: "white",
    "&:hover": {
      backgroundColor: "#d32f2f",
    },
  },
  buttonProgress: {
    color: "white",
    marginLeft: theme.spacing(1),
  },
  noProductDataText: {
    marginTop: 50,
    marginBottom: 50,
    textAlign: 'center',
  },
}));

// Add normalization for allocation configuration
function normalizeAllocationConfig(backendData, poStatuses) {
  return {
    showExpiredLots: (backendData?.showExpiredLots === true || backendData?.showExpiredLots === "YES") ? "YES" : 
                     (backendData?.showExpiredLots === false || backendData?.showExpiredLots === "NO") ? "NO" : "NO",
    defaultAllocationReq: (backendData?.defaultAllocationReq === true || backendData?.defaultAllocationReq === "YES") ? "YES" : 
                          (backendData?.defaultAllocationReq === false || backendData?.defaultAllocationReq === "NO") ? "NO" : "NO",
    qtyVarianceAcceptanceLimit: backendData?.qtyVarianceAcceptanceLimit?.toString() || "10",
    allowMultipleLotAllocation: (backendData?.allowMultipleLotAllocation === true || backendData?.allowMultipleLotAllocation === "YES") ? "YES" : 
                                (backendData?.allowMultipleLotAllocation === false || backendData?.allowMultipleLotAllocation === "NO") ? "NO" : "NO",
    allowOverAllocation: (backendData?.allowOverAllocation === undefined || backendData?.allowOverAllocation === null) ? "NA" : 
                         (backendData?.allowOverAllocation === true || backendData?.allowOverAllocation === "YES") ? "YES" : "NO",
    overAllocationLimit: (backendData?.overAllocationLimit === undefined || backendData?.overAllocationLimit === null) ? "NA" : 
                         backendData?.overAllocationLimit?.toString() || "50",
    allowUnderAllocation: (backendData?.allowUnderAllocation === undefined || backendData?.allowUnderAllocation === null) ? "NO" :
                         (backendData?.allowUnderAllocation === true || backendData?.allowUnderAllocation === "YES") ? "YES" : "NO",
    underAllocationLimit: (backendData?.allowUnderAllocationLimit === undefined || backendData?.allowUnderAllocationLimit === null) ? "10" :
                         backendData?.allowUnderAllocationLimit?.toString() || "10",
    // Allocation warning configuration (BCM-613)
    // NOTE: the backend field name spells "Message" in full (warningMessageRequired...)
    warningMsgRequiredForUnderAllocation: (backendData?.warningMessageRequiredForUnderAllocation === undefined || backendData?.warningMessageRequiredForUnderAllocation === null) ? "NO" :
                         (backendData?.warningMessageRequiredForUnderAllocation === true || backendData?.warningMessageRequiredForUnderAllocation === "YES") ? "YES" : "NO",
    underAllocationWarningRangeFrom: (backendData?.underAllocationWarningRangeFrom === undefined || backendData?.underAllocationWarningRangeFrom === null) ? "NA" :
                         backendData?.underAllocationWarningRangeFrom?.toString() || "NA",
    underAllocationWarningRangeTo: (backendData?.underAllocationWarningRangeTo === undefined || backendData?.underAllocationWarningRangeTo === null) ? "NA" :
                         backendData?.underAllocationWarningRangeTo?.toString() || "NA",
    warningMsgRequiredForOverAllocation: (backendData?.warningMessageRequiredForOverAllocation === undefined || backendData?.warningMessageRequiredForOverAllocation === null) ? "NO" :
                         (backendData?.warningMessageRequiredForOverAllocation === true || backendData?.warningMessageRequiredForOverAllocation === "YES") ? "YES" : "NO",
    overAllocationWarningRangeFrom: (backendData?.overAllocationWarningRangeFrom === undefined || backendData?.overAllocationWarningRangeFrom === null) ? "NA" :
                         backendData?.overAllocationWarningRangeFrom?.toString() || "NA",
    overAllocationWarningRangeTo: (backendData?.overAllocationWarningRangeTo === undefined || backendData?.overAllocationWarningRangeTo === null) ? "NA" :
                         backendData?.overAllocationWarningRangeTo?.toString() || "NA",
    holdTimeBtnCreatedAndSWCMO: (backendData?.holdTimeBtnCreatedAndSWCMO === undefined || backendData?.holdTimeBtnCreatedAndSWCMO === null || backendData?.holdTimeBtnCreatedAndSWCMO === 0) ? "0" :
                                backendData?.holdTimeBtnCreatedAndSWCMO?.toString() || "12",
    allowLotAllocationBy: backendData?.allowLotAllocationBy === "all" ? "Both Sponsor and CMO (CMO only if Sponsor has not allocated batch before PO is shared)" : backendData?.allowLotAllocationBy || "Sponsor",
    sponsorTriggerStart: (backendData?.sponsorTriggerStart === undefined || backendData?.sponsorTriggerStart === null) ? "NA" :
      backendData?.sponsorTriggerStart === "poStatus" ? "PO Status" :
        backendData?.sponsorTriggerStart === "scheduleMfgStartDateBefore" ? "No. of days before scheduled manufacturing start date" :
          "No. of days before expected delivery date",
    sponsorTriggerEnd: (backendData?.sponsorTriggerEnd === undefined || backendData?.sponsorTriggerEnd === null) ? "NA" :
      backendData?.sponsorTriggerEnd === "poStatus" ? "PO Status" :
        backendData?.sponsorTriggerEnd === "scheduleMfgStartDateBefore" ? "No. of days before scheduled manufacturing start date" :
          "No. of days before expected delivery date",
    cmoTriggerStart: (backendData?.cmoTriggerStart === undefined || backendData?.cmoTriggerStart === null) ? "NA" :
      backendData?.cmoTriggerStart === "poStatus" ? "PO Status" :
        backendData?.cmoTriggerStart === "scheduleMfgStartDateBefore" ? "No. of days before scheduled manufacturing start date" :
          "No. of days before expected delivery date",
    cmoTriggerEnd: (backendData?.cmoTriggerEnd === undefined || backendData?.cmoTriggerEnd === null) ? "NA" :
        backendData?.cmoTriggerEnd === "poStatus" ? "PO Status" :
        backendData?.cmoTriggerEnd === "scheduleMfgStartDateBefore" ? "No. of days before scheduled manufacturing start date" :
        "No. of days before expected delivery date",
    sponsorTriggerStartValue: (backendData?.sponsorTriggerStartValue === undefined || backendData?.sponsorTriggerStartValue === null) ? "NA" : 
                              backendData?.sponsorTriggerStartValue?.toString() || "",
    sponsorTriggerEndValue: (backendData?.sponsorTriggerEndValue === undefined || backendData?.sponsorTriggerEndValue === null) ? "NA" : 
                            backendData?.sponsorTriggerEndValue?.toString() || "",
    cmoTriggerStartValue: (backendData?.cmoTriggerStartValue === undefined || backendData?.cmoTriggerStartValue === null) ? "NA" : 
                          backendData?.cmoTriggerStartValue?.toString() || "",
    cmoTriggerEndValue: (backendData?.cmoTriggerEndValue === undefined || backendData?.cmoTriggerEndValue === null) ? "NA" : 
                        backendData?.cmoTriggerEndValue?.toString() || "",
  };
}

const SECTION_KEYS = [
  "productDetails",
  "statusSelection",
  "allocationConfiguration",
  "documentDeliverables",
  "otherConfiguration",
  "processFlowConfiguration",
];

// Helper function to sort materials - sponsor materials first, CMO-only after
// IMPORTANT: Handles duplicate bo_material_code where only first occurrence is sponsor-linked
const sortMaterials = (materialDetails) => {
  
  
  if (!materialDetails?.productMaterialsList) {
    return materialDetails;
  }
  
  // Track which bo_material_codes have been seen to identify duplicates
  // First occurrence with materialTypeName = sponsor material (linked to both sponsor and CMO tables)
  // Subsequent occurrences with same bo_material_code = CMO-only materials (no link to sponsor table)
  // Sponsor/CMO classification shared with ProductDetails (see materialSponsorLink.js) -
  // respects a boolean sponsorSupplyingMaterial, otherwise derives it from the row data.
  const sponsorLinkedFlags = getSponsorLinkedFlags(materialDetails.productMaterialsList);
  const materialsWithFlag = materialDetails.productMaterialsList.map((material, index) => ({
    ...material,
    _isSponsorLinked: sponsorLinkedFlags[index],
  }));

  
  const sorted = materialsWithFlag.sort((a, b) => {
    // Sponsor-linked materials come first, then CMO-only materials
    if (a._isSponsorLinked && !b._isSponsorLinked) return -1;
    if (!a._isSponsorLinked && b._isSponsorLinked) return 1;
    return 0;
  });
  

  
  

  
  return {
    ...materialDetails,
    productMaterialsList: sorted
  };
};

const ViewProduct = () => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const {
    selectedProduct,
    configuredProductDetails,
    viewProductDetails,
    selectedProductFormData,
    isEdit,
    isNotPending
  } = location.state || {};

  const [allFgStatuses, setAllFgStatuses] = useState(null);

  // API base URL
  const apiBaseUrl = useMemo(() => {
    const appName = localStorage.getItem("appName");
    const network = localStorage.getItem("network");
    const collabType = localStorage.getItem("collabType");
    const roleName = localStorage.getItem("roleName");

    if (appName && network && collabType && roleName) {
      return `${appUrl}/${appName}/${network}/${collabType}/${roleName}`;
    }
    return null;
  }, [
    localStorage.getItem("appName"),
    localStorage.getItem("network"),
    localStorage.getItem("collabType"),
    localStorage.getItem("roleName"),
  ]);

  // Function to fetch FG statuses for dropdown options in Document Deliverables
  const fetchFGStatuses = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/fetchFGStatusesDirect/true`);
      if (response?.data?.data) {
        setAllFgStatuses(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching FG statuses:", error);
    }
  };

  // Fetch FG statuses on component mount
  useEffect(() => {
    if (apiBaseUrl) {
      fetchFGStatuses();
    }
  }, [apiBaseUrl]);

  // Determine which product data to use
  const productDataToUse = configuredProductDetails
    || (isEdit && isNotPending && selectedProductFormData)
    || null;

  // Initialize formData with available data on first render
  const [formData, setFormData] = useState(() => {
    if (!productDataToUse) return {};
    return {
      productDetails: {
        productCode: productDataToUse?.productDetails?.productCode,
        productDescription: productDataToUse?.productDetails?.productDescription,
        brand: productDataToUse?.productDetails?.brand,
        status: productDataToUse?.productDetails?.status,
        supplyTypeID: productDataToUse?.productDetails?.supplyTypeID,
        productModality: productDataToUse?.productDetails?.drugTypeName,
        drugTypeID: productDataToUse?.productDetails?.drugTypeID,
        supplyTypeName: productDataToUse?.productDetails?.supplyTypeName,
        manufacturingFacilityType: productDataToUse?.productDetails?.facilityTypeName,
        facilityTypeID: productDataToUse?.productDetails?.facilityTypeID,
        cmoPlantId: productDataToUse?.productDetails?.vendorPlant,
        cmoProductCode: productDataToUse?.productDetails?.cmoProductCode,
        cmoProductDescription: productDataToUse?.productDetails?.cmoProductDescription,
        cmoProductDetailsList: productDataToUse?.productDetails?.cmoProductDetailsList || [],
        pendingConfiguration: productDataToUse?.productDetails?.pendingConfiguration,
        productTypeName: productDataToUse?.productDetails?.productTypeName,
        collabrixProductID: productDataToUse?.productDetails?.collabrixProductID,
        vendorMosymphonyID: productDataToUse?.productDetails?.vendorMosymphonyID,
        vendorPlant: productDataToUse?.productDetails?.vendorPlant,
        vendorName: productDataToUse?.productDetails?.vendorName,
        productTypeID: productDataToUse?.productDetails?.productTypeID,
        receivingStorageLocationIDsFromSponsorERP:
          Array.isArray(productDataToUse?.productPlantSLocMapping) &&
          productDataToUse.productPlantSLocMapping.length > 0
            ? slocsFromMapping(productDataToUse.productPlantSLocMapping)
            : productDataToUse?.productDetails?.receivingStorageLocationIDsFromSponsorERP,
        receivingPlantIDFromSponsorERP:productDataToUse?.productDetails?.receivingPlantIDFromSponsorERP,
        cmoUOM:productDataToUse?.productDetails?.cmoUOM,
        sponsorUOM:productDataToUse?.productDetails?.sponsorUOM,
        CMOIDFromSponsorERP: productDataToUse?.productDetails?.CMOIDFromSponsorERP,
        aslNumber: productDataToUse?.productDetails?.aslNumber,
        defaultASLNumber: productDataToUse?.productDetails?.defaultASLNumber,
        shipmentRequired: productDataToUse?.productDetails?.shipmentRequired,
        cmoWIPProductCode: productDataToUse?.productDetails?.cmoWIPProductCode,
        sponsorCQAApprovalReq: productDataToUse?.productDetails?.sponsorCQAApprovalReq,
        subContractingPOs: productDataToUse?.productDetails?.subContractingPOs,
      },
        materialDetails: sortMaterials(productDataToUse?.materialDetails) || { productMaterialsList: [] },
        fgStatuses: productDataToUse?.productFGStatuses?.fgStatusesJSON || productDataToUse?.fgStatuses || {},
      documentDeliverables: (productDataToUse?.documentDetailsList || []).map(doc => ({
        ...doc,
        documentTypeID: doc.documentTypeID,
        fileFormatTypeID: doc.fileFormatTypeID,
        startStatus: doc.startOfUploadStatusText || doc.startOfUploadStatus || "Not Specified",
        endStatus: doc.endOfUploadStatusText || doc.endOfUploadStatus || "Not Specified",
        orgResponsibleID: doc.uploadOrgCollaboratorTypeID,
        mandatory: doc.documentMandatoryFlag,
        orgResponsibleName: doc.orgResponsibleName || "",
      })),
      otherConfiguration: productDataToUse?.otherConfiguration || {},
      processFlowConfiguration: productDataToUse?.processFlowConfiguration || {},
      shipmentConfiguration: { dispatchDuration: productDataToUse?.shipmentConfiguration?.dispatchDuration } || productDataToUse?.shipmentConfiguration || {},
      allocationConfiguration: normalizeAllocationConfig(productDataToUse?.allocationConfiguration || {}, productDataToUse?.fgStatuses || []),
    };
  });

  // Update formData if productDataToUse changes after mount
  useEffect(() => {
    if (!productDataToUse) return;
    setFormData({
      productDetails: {
        productCode: productDataToUse?.productDetails?.productCode,
        productDescription: productDataToUse?.productDetails?.productDescription,
        brand: productDataToUse?.productDetails?.brand,
        status: productDataToUse?.productDetails?.status,
        supplyTypeID: productDataToUse?.productDetails?.supplyTypeID,
        productModality: productDataToUse?.productDetails?.drugTypeName,
        drugTypeID: productDataToUse?.productDetails?.drugTypeID,
        supplyTypeName: productDataToUse?.productDetails?.supplyTypeName,
        manufacturingFacilityType: productDataToUse?.productDetails?.facilityTypeName,
        facilityTypeID: productDataToUse?.productDetails?.facilityTypeID,
        cmoPlantId: productDataToUse?.productDetails?.vendorPlant,
        cmoProductCode: productDataToUse?.productDetails?.cmoProductCode,
        cmoProductDescription: productDataToUse?.productDetails?.cmoProductDescription,
        cmoProductDetailsList: productDataToUse?.productDetails?.cmoProductDetailsList || [],
        pendingConfiguration: productDataToUse?.productDetails?.pendingConfiguration,
        productTypeName: productDataToUse?.productDetails?.productTypeName,
        collabrixProductID: productDataToUse?.productDetails?.collabrixProductID,
        vendorMosymphonyID: productDataToUse?.productDetails?.vendorMosymphonyID,
        vendorPlant: productDataToUse?.productDetails?.vendorPlant,
        vendorName: productDataToUse?.productDetails?.vendorName,
        productTypeID: productDataToUse?.productDetails?.productTypeID,
       receivingStorageLocationIDsFromSponsorERP:
         Array.isArray(productDataToUse?.productPlantSLocMapping) &&
         productDataToUse.productPlantSLocMapping.length > 0
           ? slocsFromMapping(productDataToUse.productPlantSLocMapping)
           : productDataToUse?.productDetails?.receivingStorageLocationIDsFromSponsorERP,
        receivingPlantIDFromSponsorERP:productDataToUse?.productDetails?.receivingPlantIDFromSponsorERP,
        cmoUOM:productDataToUse?.productDetails?.cmoUOM,
        sponsorUOM:productDataToUse?.productDetails?.sponsorUOM,
        CMOIDFromSponsorERP: productDataToUse?.productDetails?.CMOIDFromSponsorERP,
        aslNumber: productDataToUse?.productDetails?.aslNumber,
        defaultASLNumber: productDataToUse?.productDetails?.defaultASLNumber,
        shipmentRequired: productDataToUse?.productDetails?.shipmentRequired,
        cmoWIPProductCode: productDataToUse?.productDetails?.cmoWIPProductCode,
        sponsorCQAApprovalReq: productDataToUse?.productDetails?.sponsorCQAApprovalReq,
        subContractingPOs: productDataToUse?.productDetails?.subContractingPOs,
      },
      materialDetails: sortMaterials(productDataToUse?.materialDetails) || { productMaterialsList: [] },
      fgStatuses: productDataToUse?.productFGStatuses?.fgStatusesJSON || productDataToUse?.fgStatuses || {},
      documentDeliverables: (productDataToUse?.documentDetailsList || []).map(doc => ({
        ...doc,
        documentTypeID: doc.documentTypeID,
        fileFormatTypeID: doc.fileFormatTypeID,
        startStatus: doc.startOfUploadStatusText || doc.startOfUploadStatus || "Not Specified",
        endStatus: doc.endOfUploadStatusText || doc.endOfUploadStatus || "Not Specified",
        orgResponsibleID: doc.uploadOrgCollaboratorTypeID,
        mandatory: doc.documentMandatoryFlag,
        orgResponsibleName: doc.orgResponsibleName || "",
      })),
      otherConfiguration: productDataToUse?.otherConfiguration || {},
      processFlowConfiguration: productDataToUse?.processFlowConfiguration || {},
      shipmentConfiguration: { dispatchDuration: productDataToUse?.shipmentConfiguration?.dispatchDuration } || productDataToUse?.shipmentConfiguration || {},
      allocationConfiguration: normalizeAllocationConfig(productDataToUse?.allocationConfiguration || {}, productDataToUse?.fgStatuses || []),
    });
  }, [productDataToUse]);

  // If data is not ready, show loading spinner
  if (!productDataToUse || !formData.productDetails) {
    return (
      <Box
        position="fixed"
        top={0}
        left={0}
        width="100vw"
        height="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgcolor="rgba(255,255,255,0.6)"
        zIndex={2000}
        style={{ zIndex: 2000 }}
      >
        <CircularProgress size={60} color="primary" />
      </Box>
    );
  }

  const handleGoBack = () => history.goBack();

  const sections = [
    {
      key: "productDetails",
      label: "Product Details",
      Component: ProductDetails,
      stepKey: "productDetails",
    },
    {
      key: "statusSelection",
      label: "Status Selection",
      Component: StatusSelection,
      stepKey: "fgStatuses",
      additionalProps: {
        cameFromBack: true
      }
    },
    {
      key: "allocationConfiguration",
      label: "Allocation Configurations",
      Component: AllocationConfigurations,
      stepKey: "allocationConfiguration",
    },
    // {
    //   key: "documentDeliverables",
    //   label: "Document Deliverables",
    //   Component: DocumentDeliverables,
    //   stepKey: "documentDeliverables",
    // },
    {
      key: "otherConfiguration",
      label: "Other Configurations",
      Component: OtherConfigurations,
      stepKey: "otherConfiguration",
    },
    {
      key: "processFlowConfiguration",
      label: "Process Flow Configuration",
      Component: ProcessFlowConfiguration,
      stepKey: "processFlowConfiguration",
    },
    {
      key: "documentDeliverables",
      label: "Document Deliverables",
      Component: DocumentDeliverables,
      stepKey: "documentDeliverables",
      additionalProps: {
        allFgStatuses: allFgStatuses, // Pass the fetched FG statuses
      }
    },
  ];

  // If edit mode, render the edit component
  if (isEdit && !viewProductDetails) {
    return <ViewProductEdit />;
  }


  return (
    <>

      <Paper className={`${classes.root} view-product-wrapper`}>
        <Box className={classes.stepperHeader}>
          <Typography className={classes.heading} variant="h5">
            View product details
          </Typography>
          <Typography className={classes.subheading}>
            Please view the product details and configurations in the fields below
          </Typography>
          <Box className={`close-page-action`} onClick={handleGoBack}>
            <Typography>Cancel</Typography>
            <CloseIcon />
          </Box>
        </Box>
        <div className="view-product-wrapper-content">
          <div className={`stepper-container config-product-stepper-wrapper `}>
            {selectedProduct ? (
              sections.map(({ key, label, Component, stepKey, additionalProps = {} }, idx) => (
                <Paper elevation={0} className={classes.sectionPaper} key={key}>
                  <Box className={classes.reviewSection}>
                    <Component
                      formData={formData}
                      classes={classes}
                      readOnly={true}
                      stepsLength={1}
                      {...additionalProps}
                    />
                  </Box>
                  {idx < sections.length - 1 && <Divider />}
                </Paper>
              ))
            ) : (
              <Typography color="error" className={classes.noProductDataText}>
                No product data available. Please select a product from the list.
              </Typography>
            )}
          </div>
        </div>
      </Paper>
    </>
  );
};

export default ViewProduct;
